import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { FOUNDING_OWNER_ID } from '../../utils/constants';
import api from '../../api';

export default function KeysPage() {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [noteInput, setNoteInput] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);

  const isOwner = user?.discord_id === FOUNDING_OWNER_ID;
  const isPartner = !isOwner && (user?.bot_tier === 'partner' || user?.bot_tier === 'owner');
  const hasAccess = isOwner || isPartner;

  useEffect(() => {
    if (!hasAccess) return;
    api.get('/api/keys')
      .then(res => setKeys(res.data))
      .catch(err => console.error('Keys load error:', err))
      .finally(() => setLoading(false));
  }, [isOwner]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/api/keys/generate', { note: noteInput || null });
      setKeys(prev => [res.data, ...prev]);
      setNoteInput('');
      setShowForm(false);
    } catch (err) {
      alert('Failed to generate key: ' + (err.response?.data?.error || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Revoke this key? The server using it will lose access immediately.')) return;
    setRevoking(keyId);
    try {
      await api.delete(`/api/keys/${keyId}`);
      setKeys(prev => prev.map(k => k.id === keyId ? { ...k, status: 'revoked' } : k));
    } catch (err) {
      alert('Failed to revoke key.');
    } finally {
      setRevoking(null);
    }
  };

  const handleDelete = async (keyId) => {
    if (!window.confirm('Permanently delete this key? This cannot be undone and removes it from the database entirely.')) return;
    setDeleting(keyId);
    try {
      await api.delete(`/api/keys/${keyId}/permanent`);
      setKeys(prev => prev.filter(k => k.id !== keyId));
    } catch (err) {
      alert('Failed to delete key.');
    } finally {
      setDeleting(null);
    }
  };

  const handleCopy = (key) => {
    navigator.clipboard.writeText(key.key_string);
    setCopiedId(key.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (!hasAccess) return (
    <div style={{ textAlign: 'center', padding: '80px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '60px', marginBottom: '20px' }}>🔒</div>
      <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>Access Restricted</div>
      <div style={{ color: 'var(--text-muted)' }}>This section is restricted to owners and partners.</div>
    </div>
  );

  const activeKeys = keys.filter(k => k.status === 'active');
  const usedKeys = keys.filter(k => k.status === 'used');
  const revokedKeys = keys.filter(k => k.status === 'revoked');
  const filteredKeys = filter === 'all' ? keys : keys.filter(k => k.status === filter);

  const statusConfig = {
    active: { color: 'var(--accent-primary)', label: 'Available', emoji: '🔑' },
    used: { color: 'var(--success)', label: 'In Use', emoji: '✅' },
    revoked: { color: 'var(--text-muted)', label: 'Revoked', emoji: '❌' },
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>
            License Keys
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
            {activeKeys.length} available · {usedKeys.length} in use · {revokedKeys.length} revoked
          </p>
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{
          padding: '12px 24px', background: 'var(--gradient-accent)',
          color: '#fff', border: 'none', borderRadius: '12px',
          fontSize: '14px', fontWeight: 700, cursor: 'pointer',
          fontFamily: 'var(--font-display)', boxShadow: 'var(--shadow)',
        }}>
          {showForm ? '✕ Cancel' : '+ Generate Key'}
        </button>
      </div>

      {/* Generate form */}
      {showForm && (
        <div style={{
          background: 'var(--bg-card)', border: '2px solid var(--accent-primary)',
          borderRadius: '16px', padding: '20px', marginBottom: '24px',
          boxShadow: 'var(--shadow-card)',
        }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--text-primary)', marginBottom: '14px' }}>
            Generate New Key
          </h3>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                Note (optional — e.g. server name)
              </label>
              <input
                type="text"
                placeholder="e.g. MASH Server, Mystique..."
                value={noteInput}
                onChange={e => setNoteInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                style={{
                  width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                  border: '2px solid var(--border)', borderRadius: '10px',
                  fontSize: '14px', fontFamily: 'var(--font-body)',
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)', outline: 'none',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>
            <button onClick={handleGenerate} disabled={generating} style={{
              padding: '10px 24px', background: 'var(--gradient-accent)',
              color: '#fff', border: 'none', borderRadius: '10px',
              fontSize: '14px', fontWeight: 700,
              cursor: generating ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-display)', flexShrink: 0,
            }}>
              {generating ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      {keys.length > 0 && (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'var(--bg-secondary)', borderRadius: '12px', padding: '4px', width: 'fit-content' }}>
          {[
            { key: 'all', label: `All (${keys.length})` },
            { key: 'active', label: `Available (${activeKeys.length})` },
            { key: 'used', label: `In Use (${usedKeys.length})` },
            { key: 'revoked', label: `Revoked (${revokedKeys.length})` },
          ].map(t => (
            <button key={t.key} onClick={() => setFilter(t.key)} style={{
              padding: '8px 16px', borderRadius: '8px', cursor: 'pointer',
              border: 'none', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 600,
              background: filter === t.key ? 'var(--bg-card)' : 'transparent',
              color: filter === t.key ? 'var(--text-primary)' : 'var(--text-muted)',
              boxShadow: filter === t.key ? 'var(--shadow-card)' : 'none',
              transition: 'all 0.15s',
            }}>{t.label}</button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>
          Loading keys...
        </div>
      ) : keys.length === 0 ? (
        <div style={{
          background: 'var(--bg-card)', border: '2px dashed var(--border)',
          borderRadius: '16px', padding: '60px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔑</div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>No keys yet</div>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Generate a key to share with a server owner.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filteredKeys.map(key => {
            const cfg = statusConfig[key.status] || statusConfig.active;
            return (
              <div key={key.id} style={{
                background: 'var(--bg-card)',
                border: `1px solid ${key.status === 'used' ? 'var(--success)30' : 'var(--border)'}`,
                borderRadius: '14px', padding: '16px 20px',
                display: 'flex', alignItems: 'center', gap: '16px',
                boxShadow: 'var(--shadow-card)',
                opacity: key.status === 'revoked' ? 0.5 : 1,
              }}>
                <div style={{ fontSize: '22px', flexShrink: 0 }}>{cfg.emoji}</div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Key string */}
                  <div style={{
                    fontFamily: 'monospace', fontSize: '15px', fontWeight: 700,
                    color: 'var(--text-primary)', letterSpacing: '1px', marginBottom: '4px',
                  }}>
                    {key.key_string}
                  </div>
                  {/* Meta info */}
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                    {key.note && <span>📝 {key.note}</span>}
                    {key.used_by_guild_name && <span>🏠 {key.used_by_guild_name}</span>}
                    {key.activated_at && <span>✅ Activated {new Date(key.activated_at).toLocaleDateString()}</span>}
                    <span>Generated {new Date(key.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Status badge */}
                <div style={{
                  fontSize: '11px', fontWeight: 700, padding: '4px 12px',
                  borderRadius: '100px', flexShrink: 0,
                  background: cfg.color + '20',
                  color: cfg.color,
                }}>
                  {cfg.label}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  {key.status !== 'revoked' && (
                    <button onClick={() => handleCopy(key)} style={{
                      padding: '8px 14px', borderRadius: '8px', cursor: 'pointer',
                      border: '1px solid var(--border)',
                      background: copiedId === key.id ? 'var(--success)' : 'var(--bg-secondary)',
                      fontSize: '12px',
                      color: copiedId === key.id ? '#fff' : 'var(--text-secondary)',
                      fontFamily: 'var(--font-body)', transition: 'all 0.2s',
                    }}>
                      {copiedId === key.id ? '✓ Copied' : '📋 Copy'}
                    </button>
                  )}
                  {key.status === 'active' && isOwner && (
                    <button onClick={() => handleRevoke(key.id)} disabled={revoking === key.id} style={{
                      padding: '8px 14px', borderRadius: '8px', cursor: 'pointer',
                      border: '1px solid var(--warning)', background: 'transparent',
                      fontSize: '12px', color: 'var(--warning)',
                      fontFamily: 'var(--font-body)',
                    }}>
                      {revoking === key.id ? '...' : 'Revoke'}
                    </button>
                  )}
                  {isOwner && (
                  <button onClick={() => handleDelete(key.id)} disabled={deleting === key.id} title="Permanently delete"
                    style={{
                      padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
                      border: '1px solid var(--danger)', background: 'transparent',
                      fontSize: '12px', color: 'var(--danger)',
                      fontFamily: 'var(--font-body)',
                    }}>
                    {deleting === key.id ? '...' : '🗑️'}
                  </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
